import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CompleteUsernameAccountProfileSetupDto,
  ProfileViewDto,
  SessionViewDto,
  UpdateMfaDto,
  UpdatePasswordDto,
  UpdateProfileDto,
} from '../common/dto/account.dto';
import { MFASetupCompleteDto, MfaSetupViewDto, MFAVerifyDto, OrganisationInviteActionDto, OrgInviteActionViewDto } from '../common/dto/auth.dto';
import { SimpleViewDto, StatusDto } from '../common/dto/common.dto';
import customFetchBase from './base.service';

const API_URI = import.meta.env.VITE_UAA_SERVICE;

export const accountApi = createApi({
  reducerPath: 'accountApi',
  baseQuery: customFetchBase(`${API_URI}/account`),
  tagTypes: [],
  endpoints: (builder) => ({
    completeProfile: builder.mutation<StatusDto, Partial<CompleteUsernameAccountProfileSetupDto>>({
      query(body) {
        return {
          url: `complete-profile-setup`,
          method: 'POST',
          body,
        };
      },
      // invalidatesTags: [{ type: 'ResetPassword' }],
    }),
    getInvitationsPending: builder.query<SimpleViewDto[], void>({
      query() {
        return {
          url: `pending-invitations`,
          method: 'GET',
        };
      },
      // invalidatesTags: [{ type: 'ResetPassword' }],
    }),
    respondToOrganisationInvite: builder.mutation<OrgInviteActionViewDto, OrganisationInviteActionDto>({
      query(body) {
        return {
          url: 'invite-response',
          method: 'PUT',
          body,
        };
      },
    }),
    getAllSessions: builder.query<SessionViewDto[], void>({
      query() {
        return {
          url: 'sessions',
          method: 'GET',
        };
      },
    }),
    deleteSessionsByIds: builder.mutation<void, string[]>({
      query(ids) {
        return {
          url: 'delete-sessions',
          method: 'PUT',
          body: ids,
        };
      },
    }),
    getProfile: builder.query<ProfileViewDto, void>({
      query() {
        return {
          url: 'profile',
          method: 'GET',
        };
      },
    }),
    updateProfile: builder.mutation<ProfileViewDto, UpdateProfileDto>({
      query(body) {
        return {
          url: 'update-profile',
          method: 'PUT',
          body,
        };
      },
    }),
    updatePassword: builder.mutation<StatusDto, UpdatePasswordDto>({
      query(body) {
        return {
          url: 'update-password',
          method: 'PUT',
          body,
        };
      },
    }),
    getMfaSetup: builder.query<MfaSetupViewDto, void>({
      query() {
        return {
          url: 'mfa-setup',
          method: 'GET',
        };
      },
    }),
    updateTwoFactorAuth: builder.mutation<ProfileViewDto, UpdateMfaDto>({
      query(body) {
        return {
          url: 'update-mfa',
          method: 'PUT',
          body,
        };
      },
    }),
    completeMfaSetup: builder.mutation<StatusDto, MFASetupCompleteDto>({
      query(body) {
        return {
          url: 'mfa-setup-complete',
          method: 'PUT',
          body,
        };
      },
    }),
    verifyMfa: builder.mutation<StatusDto, MFAVerifyDto>({
      query(body) {
        return {
          url: 'mfa-verify',
          method: 'POST',
          body,
        };
      },
    }),
    logout: builder.query<StatusDto, void>({
      query() {
        return {
          url: 'logout',
          method: 'GET',
        };
      },
    }),
  }),
});

// auto-generated based on the defined endpoints
export const {
  useUpdateTwoFactorAuthMutation,
  useCompleteProfileMutation,
  useLazyGetInvitationsPendingQuery,
  useRespondToOrganisationInviteMutation,
  useLazyGetAllSessionsQuery,
  useDeleteSessionsByIdsMutation,
  useUpdateProfileMutation,
  useUpdatePasswordMutation,
  useLazyGetProfileQuery,
  useGetMfaSetupQuery,
  useCompleteMfaSetupMutation,
  useVerifyMfaMutation,
  useLazyLogoutQuery,
} = accountApi;
